import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
// Using API types for ServiceStore entities
import type { Bill } from "types/common/ServiceStoreTypes";
// Keep local types for UI-specific data
import { BillStatus } from "../../types";

// Import types from BillStore
export interface BillFormData {
  id?: string;
  vendorId: string;
  billNumber: string;
  referenceNumber?: string;
  billDate: string; // YYYY-MM-DD
  dueDate: string; // YYYY-MM-DD
  status: BillStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
  lineItems: BillLineItem[];
}

export interface BillLineItem {
  id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

class BillsStore {
  rootStore: RootStore;

  // Observables - Only UI state
  isFormOpen = false;
  selectedBillId: string | null = null;
  formData: BillFormData = this.getInitialFormData();
  isEditMode = false;
  selectedBillIds: string[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Get service store
  get billsStore(): ServiceStore {
    return this.rootStore.billsStore as ServiceStore;
  }

  // Computed values
  get selectedBill(): Bill | null {
    return this.selectedBillId
      ? this.billsStore.entities.find((b: any) => b.id === this.selectedBillId) as unknown as Bill
      : null;
  }

  get bills(): Bill[] {
    return (this.billsStore.entities as unknown as Bill[]) || [];
  }

  get isLoading(): boolean {
    return this.billsStore.isServicePending;
  }

  get totalPayables(): number {
    return this.bills
      .filter((bill) => bill.status !== "paid")
      .reduce((sum, bill) => sum + bill.totalAmount, 0);
  }

  get overdueBills(): Bill[] {
    const today = new Date().toISOString().split("T")[0];
    return this.bills.filter(
      (bill) => bill.status !== BillStatus.PAID && (bill as any).dueDate < today
    );
  }

  get billsByStatus() {
    return {
      draft: this.bills.filter((bill) => bill.status === BillStatus.DRAFT)
        .length,
      received: this.bills.filter((bill) => bill.status === BillStatus.RECEIVED)
        .length,
      paid: this.bills.filter((bill) => bill.status === BillStatus.PAID).length,
      overdue: this.overdueBills.length,
    };
  }

  get formattedBills(): Bill[] {
    return this.bills.map((bill) => ({
      ...bill,
      formattedSubtotal: this.formatCurrency(bill.subtotal),
      formattedTaxAmount: this.formatCurrency(bill.taxAmount),
      formattedTotalAmount: this.formatCurrency(bill.totalAmount),
    }));
  }

  // Form management
  openCreateForm = () => {
    this.formData = this.getInitialFormData();
    this.isEditMode = false;
    this.isFormOpen = true;
    this.selectedBillId = null;
  };

  openEditForm = (billId: string) => {
    const bill = this.billsStore.entities.find((b: any) => b.id === billId) as unknown as Bill;
    if (!bill) return;

    this.formData = {
      id: bill.id,
      vendorId: (bill as any).vendorId,
      billNumber: bill.billNumber,
      referenceNumber: bill.referenceNumber || "",
      billDate: (bill as any).billDate,
      dueDate: (bill as any).dueDate,
      status: bill.status,
      subtotal: bill.subtotal,
      taxAmount: bill.taxAmount,
      totalAmount: bill.totalAmount,
      notes: bill.notes || "",
      lineItems: (bill as any).lineItems || [],
    };
    this.isEditMode = true;
    this.isFormOpen = true;
    this.selectedBillId = billId;
  };

  closeForm = () => {
    this.isFormOpen = false;
    this.isEditMode = false;
    this.selectedBillId = null;
    this.formData = this.getInitialFormData();
  };

  updateFormData = (
    field: keyof BillFormData,
    value: string | BillStatus | BillLineItem[]
  ) => {
    this.formData = { ...this.formData, [field]: value };
    if (field === "lineItems") {
      this.calculateTotals();
    }
  };

  // Line item management
  addLineItem = () => {
    const newLineItem: BillLineItem = {
      description: "",
      quantity: 1,
      unitPrice: 0,
      total: 0,
    };
    this.formData.lineItems = [...this.formData.lineItems, newLineItem];
  };

  removeLineItem = (index: number) => {
    this.formData.lineItems = this.formData.lineItems.filter(
      (_, i) => i !== index
    );
    this.calculateTotals();
  };

  updateLineItem = (
    index: number,
    field: keyof BillLineItem,
    value: string | number
  ) => {
    const updatedLineItems = [...this.formData.lineItems];
    updatedLineItems[index] = { ...updatedLineItems[index], [field]: value };

    // Recalculate total for this line item
    if (field === "quantity" || field === "unitPrice") {
      const lineItem = updatedLineItems[index];
      lineItem.total = lineItem.quantity * lineItem.unitPrice;
    }

    this.formData.lineItems = updatedLineItems;
    this.calculateTotals();
  };

  calculateTotals = () => {
    const subtotal = this.formData.lineItems.reduce(
      (sum, item) => sum + item.total,
      0
    );
    this.formData.subtotal = subtotal;
    this.formData.totalAmount = subtotal + this.formData.taxAmount;
  };

  // CRUD operations
  saveBill = async () => {
    try {
      if (this.isEditMode && this.formData.id) {
        await this.billsStore.patch(parseInt(this.formData.id), this.formData);
      } else {
        await this.billsStore.create(this.formData);
      }

      this.closeForm();
    } catch (error) {
      console.error("Failed to save bill:", error);
      throw error;
    }
  };

  deleteBill = async (billId: string) => {
    try {
      await this.billsStore.remove(parseInt(billId));
    } catch (error) {
      console.error("Failed to delete bill:", error);
      throw error;
    }
  };

  updateBillStatus = async (billId: string, status: string) => {
    try {
      await this.billsStore.patch(parseInt(billId), { status });
    } catch (error) {
      console.error("Failed to update bill status:", error);
      throw error;
    }
  };

  // Selection management
  toggleBillSelection = (billId: string) => {
    if (this.selectedBillIds.includes(billId)) {
      this.selectedBillIds = this.selectedBillIds.filter((id) => id !== billId);
    } else {
      this.selectedBillIds = [...this.selectedBillIds, billId];
    }
  };

  selectAllBills = () => {
    this.selectedBillIds = this.bills.map((bill) => bill.id);
  };

  clearSelection = () => {
    this.selectedBillIds = [];
  };

  bulkUpdateStatus = async (status: string) => {
    try {
      const updatePromises = this.selectedBillIds.map((billId) =>
        this.billsStore.patch(parseInt(billId), { status })
      );
      await Promise.all(updatePromises);
      this.clearSelection();
    } catch (error) {
      console.error("Failed to bulk update bill status:", error);
      throw error;
    }
  };

  // Private methods

  async loadBills() {
    try {
      await this.billsStore.find({});
    } catch (error) {
      console.error("Failed to load bills:", error);
      throw error;
    }
  }

  private getInitialFormData(): BillFormData {
    return {
      vendorId: "",
      billNumber: "",
      referenceNumber: "",
      billDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      status: BillStatus.DRAFT,
      subtotal: 0,
      taxAmount: 0,
      totalAmount: 0,
      notes: "",
      lineItems: [],
    };
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

export default BillsStore;
